import { useSyncExternalStore } from "react";

/**
 * Theme state for the dashboard. Applies a `data-theme` attribute on <html>
 * so the CSS variable cascade in app.css / tokens.css flips every chrome token
 * at once.
 *
 * Light + dark palettes both exist as CSS variables, but LIGHT MODE IS NOT YET
 * PREPPED. While {@link THEME_SWITCHING_ENABLED} is false we hard-lock to dark:
 * the nav toggle is hidden AND this resolver forces dark, ignoring any stale
 * "light" a browser stored from a past toggle. Flip that one constant to true
 * when light mode is ready — the toggle returns and a stored choice is honoured
 * again.
 */
export type Theme = "light" | "dark";

/**
 * Master switch for user-facing theme switching. Light mode is authored but not
 * client-ready, so this stays false: the toggle is hidden (section-tree.tsx) and
 * dark is forced everywhere (resolveInitialTheme below, plus a data-theme pin in
 * index.html). Flip to true — in this one place — to revive light mode.
 */
export const THEME_SWITCHING_ENABLED = true;

const STORAGE_KEY = "ow.theme";

function resolveInitialTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  // Light mode isn't prepped — force dark and ignore any stale "light" left in
  // localStorage, so no browser is stuck light with no visible way back.
  if (!THEME_SWITCHING_ENABLED) return "dark";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  // Dark is the default; a stored choice wins.
  return "dark";
}

function applyTheme(theme: Theme) {
  if (typeof document !== "undefined") document.documentElement.setAttribute("data-theme", theme);
}

/* ── Shared theme store ─────────────────────────────────────────────────────
   useTheme was a per-component `useState`, so each of the ~29 call sites held an
   INDEPENDENT copy of the theme. The single master toggle in dashboard.tsx flipped
   `data-theme` (so CSS chrome updated everywhere) but could NOT update any other
   component's `isLight` — those stayed frozen at their mount-time value until the
   component happened to re-mount. Anything JS-driven by `isLight` (map ramps,
   loaders, the methodology modal's category twins) went stale on a live toggle.

   This is now ONE module-level store with a subscriber set, read through
   useSyncExternalStore, so a single setTheme updates every consumer in the same
   commit — the whole app flips live. The [theme, setTheme] signature is unchanged,
   so all call sites (and ThemeToggle) work untouched. */
let currentTheme: Theme = resolveInitialTheme();
const listeners = new Set<() => void>();
applyTheme(currentTheme); // pin first paint (index.html also sets data-theme)

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function setThemeGlobal(next: Theme) {
  if (next === currentTheme) return;
  currentTheme = next;
  applyTheme(next);
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // localStorage may be unavailable (private mode) — non-fatal.
  }
  for (const cb of listeners) cb();
}

export function useTheme(): [Theme, (next: Theme) => void] {
  const theme = useSyncExternalStore(
    subscribe,
    () => currentTheme,
    () => "dark" as Theme,
  );
  return [theme, setThemeGlobal];
}

/**
 * Tiny sun / moon toggle button. Drop into any nav's utility area.
 *
 * On click toggles the theme. Visually: a small square button with an
 * icon that reflects the OPPOSITE of the current theme (i.e. when in
 * light mode the icon is a moon, signalling "switch to dark").
 *
 * Styled to work on both light and dark surfaces — uses currentColor for
 * the icon so the parent controls the colour.
 */
export function ThemeToggle({
  theme,
  setTheme,
}: {
  theme: Theme;
  setTheme: (next: Theme) => void;
  /** Deprecated / ignored. The toggle now colours itself from the ambient nav
   *  tokens (--tq-text-hi / --tq-mute), which the sidebar flips per theme, so it
   *  always matches the surface it sits on in either mode and can't desync from
   *  the visible theme. Kept in the type so existing call sites still type-check. */
  variant?: "dark-surface" | "light-surface";
}) {
  const next: Theme = theme === "dark" ? "light" : "dark";
  const isDark = theme === "dark";
  const label = `Switch to ${next} mode`;
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() => setTheme(next)}
      style={{
        width: 32,
        height: 32,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        // Colour is driven by the AMBIENT nav tokens (the sidebar sets --tq-* per
        // theme), so the toggle always matches the rail it sits on — light ink on
        // the dark rail, navy ink on the light rail — regardless of the JS theme
        // prop. That prop only picks the sun/moon glyph below.
        background: "var(--tq-mute, rgba(120,130,150,0.12))",
        border: "none",
        borderRadius: 3,
        cursor: "pointer",
        color: "var(--tq-text-hi, currentColor)",
        transition: "background 120ms, color 120ms",
        padding: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--tq-mute-strong, rgba(120,130,150,0.2))";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "var(--tq-mute, rgba(120,130,150,0.12))";
      }}
    >
      {isDark ? (
        /* Sun icon — currently dark, click to go light */
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="8" cy="8" r="3" fill="currentColor" />
          <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
            <line x1="8" y1="1.5" x2="8" y2="3" />
            <line x1="8" y1="13" x2="8" y2="14.5" />
            <line x1="1.5" y1="8" x2="3" y2="8" />
            <line x1="13" y1="8" x2="14.5" y2="8" />
            <line x1="3.4" y1="3.4" x2="4.5" y2="4.5" />
            <line x1="11.5" y1="11.5" x2="12.6" y2="12.6" />
            <line x1="3.4" y1="12.6" x2="4.5" y2="11.5" />
            <line x1="11.5" y1="4.5" x2="12.6" y2="3.4" />
          </g>
        </svg>
      ) : (
        /* Moon icon — currently light, click to go dark */
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M13 9.5A5.5 5.5 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5z" fill="currentColor" />
        </svg>
      )}
    </button>
  );
}
